import React, { useEffect, useState, useCallback, useContext } from 'react'
import { CardElement, useStripe, useElements } from '@stripe/react-stripe-js'
import { X, Loader2, Lock } from 'lucide-react'

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogClose,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'

import logger from '../../utils/logger'
import { getAllPlansService, saveCardInfoService } from '../../services'

export default function TrialModal({ open, onClose }) {
  const stripe = useStripe()
  const elements = useElements()
  const [loading, setLoading] = useState(false)
  const [message, setMessage] = useState('')
  const [allSubscriptionPlans, setAllSubscriptionPlans] = useState([])
  const [selectedStripePriceId, setSelectedStripePriceId] = useState('')

  const fetchAllSubscriptionPlan = useCallback(async () => {
    setLoading(true)
    setMessage('')

    try {
      // 1. THIS IS THE CRUCIAL CHANGE:
      // Assume getAllPlansService directly returns the array of plans (response.data from the backend)
      const plansData = await getAllPlansService()

      logger.log('Plans fetched from service:', plansData) // Debugging log

      // 2. Validate that plansData is indeed an array
      if (!Array.isArray(plansData)) {
        logger.error('API response for plans is not an array:', plansData)
        setMessage('Failed to load subscription plans. Unexpected API response format.')
        setLoading(false)
        return // Stop execution if data is malformed
      }

      // Filter plans to include only Basic Tier and Premium Tier
      // Assuming 'status' filter for 'Active' is handled by backend or is not needed here
      const filteredPlans = plansData.filter(
        (plan) => plan.tier === 'basic' || plan.tier === 'premium'
      )
      setAllSubscriptionPlans(filteredPlans)

      // Set the default selected price ID
      // Find the first active plan (basic/premium) that has a 'price' object
      const firstPlanWithPrice = filteredPlans.find(
        (plan) => plan.price && (plan.price.monthly || plan.price.yearly)
      )

      if (firstPlanWithPrice) {
        // Prioritize monthly if available, else yearly
        const defaultPriceId =
          firstPlanWithPrice.price.monthly?.priceId || firstPlanWithPrice.price.yearly?.priceId
        if (defaultPriceId) {
          setSelectedStripePriceId(defaultPriceId)
          logger.log(
            'Default plan selected:',
            firstPlanWithPrice.name,
            defaultPriceId ? 'Monthly/Yearly' : 'No valid price found'
          )
        } else {
          setMessage('No valid pricing options found for default plan.')
          logger.warn('Default plan has no monthly or yearly price ID:', firstPlanWithPrice)
          setSelectedStripePriceId('') // Ensure no price is selected if default is invalid
        }
      } else {
        setSelectedStripePriceId('')
        setMessage('No active basic or premium plans with pricing available for selection.')
        logger.warn('No active basic/premium plans with prices found in API response.')
      }

      logger.log('Fetched and filtered plans:', filteredPlans)
    } catch (error) {
      logger.error('Fetch all subscription plan error:', error.message, error.stack, error)
      setMessage('Failed to load subscription plans. Please try again later.')
    } finally {
      setLoading(false) // Always reset loading state
    }
  }, []) // Empty dependency array because fetchAllSubscriptionPlan is now memoized by useCallback

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)
    setMessage('')

    if (!stripe || !elements) {
      setMessage('Stripe has not loaded yet. Please try again.')
      setLoading(false)
      return
    }

    if (!selectedStripePriceId) {
      setMessage('Please select a subscription plan and pricing option.')
      setLoading(false)
      return
    }

    const cardElement = elements.getElement(CardElement)

    if (!cardElement) {
      setMessage('Card details are missing. Please enter your card information.')
      setLoading(false)
      return
    }

    const { error, paymentMethod } = await stripe.createPaymentMethod({
      type: 'card',
      card: cardElement,
    })

    if (error) {
      setMessage(error.message || 'An error occurred with your card details.')
      setLoading(false)
      return
    }

    try {
      const responseData = await saveCardInfoService({
        paymentMethodId: paymentMethod.id,
        priceId: selectedStripePriceId,
      })

      if (responseData.success) {
        setMessage('✅ Card saved! Your free trial will begin shortly.')
        setTimeout(() => {
          onClose()
          window.location.reload()
        }, 2000)
      } else {
        setMessage(responseData.message || 'Failed to process your trial. Please try again.')
      }
    } catch (err) {
      setMessage(err.message || 'A server error occurred. Please try again later.')
      logger.error('Error in handleSubmit (TrialModal):', err)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchAllSubscriptionPlan()
  }, [fetchAllSubscriptionPlan]) // Add fetchAllSubscriptionPlan to dependency array

  return (
    <Dialog open={open} onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        className="fixed left-[50%] top-[50%] z-50 grid translate-x-[-50%] translate-y-[-50%]
                   w-[95vw] md:w-[80vw] lg:w-[70vw] xl:w-[60vw] max-w-[900px]
                   max-h-[95vh] overflow-y-auto
                   bg-white p-6 sm:p-8 md:p-10 lg:p-12
                   rounded-lg shadow-xl animate-in fade-in-0 zoom-in-95 data-[state=closed]:zoom-out-95 data-[state=closed]:fade-out-0"
      >
        <DialogHeader className="w-full max-w-lg mx-auto text-center relative mb-6">
          <DialogTitle className="text-3xl md:text-4xl font-extrabold text-gray-900">
            Start Your Free Trial
          </DialogTitle>
          <p className="text-lg text-gray-700 mt-2">
            Enter your card details to begin your trial. You won't be charged until after the trial
            ends.
          </p>
          {/* <DialogClose className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground">
            <X className="h-6 w-6" />
            <span className="sr-only">Close</span>
          </DialogClose> */}
        </DialogHeader>

        <form
          onSubmit={handleSubmit}
          className="w-full max-w-md mx-auto space-y-6 mt-8 p-4 bg-gray-50 rounded-lg shadow-inner"
        >
          {/* Plan Selection Dropdown */}
          {allSubscriptionPlans.length > 0 ? (
            <div className="space-y-2">
              <label htmlFor="plan-select" className="block text-sm font-medium text-gray-700">
                Choose Your Plan & Pricing
              </label>
              <Select value={selectedStripePriceId} onValueChange={setSelectedStripePriceId}>
                <SelectTrigger
                  id="plan-select"
                  className="w-full border-2 border-gray-300 rounded-lg bg-white shadow-md
                             focus-within:border-button-purple transition-all duration-200
                             h-[48px] px-4 text-base"
                >
                  <SelectValue placeholder="Select a plan and pricing" />
                </SelectTrigger>
                <SelectContent>
                  {allSubscriptionPlans.map((plan) =>
                    // Ensure 'plan.price' exists before trying to access 'monthly' or 'yearly'
                    plan.price ? (
                      <React.Fragment key={plan._id}>
                        {plan.price.monthly && (
                          <SelectItem
                            key={`${plan._id}-monthly`}
                            value={plan.price.monthly.priceId}
                          >
                            {plan.name} - Monthly ({plan.price.monthly.currency.toUpperCase()}{' '}
                            {(plan.price.monthly.amount / 100).toFixed(2)})
                          </SelectItem>
                        )}
                        {plan.price.yearly && (
                          <SelectItem key={`${plan._id}-yearly`} value={plan.price.yearly.priceId}>
                            {plan.name} - Yearly ({plan.price.yearly.currency.toUpperCase()}{' '}
                            {(plan.price.yearly.amount / 100).toFixed(2)})
                          </SelectItem>
                        )}
                      </React.Fragment>
                    ) : (
                      <SelectItem key={`${plan._id}-no-prices`} value="" disabled>
                        {plan.name} (No pricing available)
                      </SelectItem>
                    )
                  )}
                </SelectContent>
              </Select>
            </div>
          ) : (
            <div className="text-center text-gray-500 py-4">
              {loading ? (
                <span className="flex items-center justify-center">
                  <Loader2 className="mr-2 h-5 w-5 animate-spin" /> Loading plans...
                </span>
              ) : (
                <p>{message || 'No subscription plans found.'}</p>
              )}
            </div>
          )}

          <label htmlFor="card-element" className="block text-sm font-medium text-gray-700 mb-2">
            Credit or Debit Card Details
          </label>
          <div className="border-2 border-gray-300 rounded-lg p-4 bg-white shadow-md focus-within:border-button-purple transition-all duration-200">
            <CardElement
              id="card-element"
              options={{
                style: {
                  base: {
                    fontSize: '16px',
                    color: '#333',
                    '::placeholder': { color: '#888' },
                  },
                  invalid: { color: '#ef4444' },
                },
                hidePostalCode: true,
              }}
              onChange={() => setMessage('')}
            />
          </div>

          <div className="flex items-center justify-center text-sm text-gray-600 mt-2">
            <Lock size={16} className="mr-1 text-gray-500" />
            <span>Securely processed by </span>
            <span className="font-semibold ml-1">Stripe</span>
          </div>

          {message && (
            <p
              className={`text-center text-sm ${message.startsWith('✅') ? 'text-green-600' : 'text-red-600'} font-medium`}
            >
              {message}
            </p>
          )}
          <Button
            type="submit"
            className="w-full bg-[#4c2859] text-white py-3 rounded-md hover:bg-[#643d73] transition-colors duration-200 text-lg font-semibold shadow-md"
            disabled={!stripe || loading || !selectedStripePriceId}
          >
            {loading ? (
              <span className="flex items-center justify-center">
                <Loader2 className="mr-2 h-5 w-5 animate-spin" /> Processing...
              </span>
            ) : (
              'Save Card & Start Trial'
            )}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  )
}
